import { useState, useEffect, createElement } from 'react';
import { useLocation } from "react-router-dom";
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { List, ListItem, ListItemButton, ListItemText, Collapse, ListItemIcon, Typography, Paper, Tooltip } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { menuIcons } from 'menu-items';
import { ListItemButtonWrapper, PopoverPaper } from './Sidebar.Drawer';

function applyRoles(item, userRoles) {
  if(typeof item?.visible === "function")
    return item?.visible(userRoles);

  return item?.roles?.filter(x => userRoles.includes(x)).length > 0;
}


export function MenuIconList({ menuItem, drawerOpen, handlePopoverOpen, handleClick }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { userToken } = useSelector(state => state.sharedState);
  const [collapseOpen, setCollapseOpen] = useState(false);

  useEffect(() => {
    if (!drawerOpen) {
      setCollapseOpen(false);
    }
  }, [drawerOpen]);

  const handleCollapse = (event) => {
    if (drawerOpen) {
      if (collapseOpen === event.currentTarget.id) {
        setCollapseOpen(false);
      } else {
        setCollapseOpen(event.currentTarget.id);
      }

    } else {
      handlePopoverOpen(event);
    }
  }

  const handleCollapseAndClick = (event, item) => {
    if (drawerOpen) {
      if (collapseOpen === event.currentTarget.id) {
        setCollapseOpen(false);
      } else {
        setCollapseOpen(event.currentTarget.id);
      }
    }
    
    if (item?.url) {
        handleClick(item.url);
    }
  }

  const selectMenuItem = (item, pathname) => {
    const { id } = item;
    const matchItem = pathname.split('/')[2] === id;

    return matchItem;

  }

  const selectMenuSubItem = (subItem, pathname) => {
    const { url, id } = subItem;

    const matchUrl = url.includes(pathname.split('/')[3]);
    const matchId = pathname.includes(id);
    return matchId || matchUrl;

  }

  const renderAvatar = (item) => {
    return createElement(menuIcons[item.icon], { style: { color: 'white', fontSize: 30 } });
  }

  const collapseOpenOrClose = (path, menus) => {
    const mainMenuPath = path.split('/')[2];
    const subMenuPath = path.split('/')[3];

    if (mainMenuPath === 'dashboard') {
        setCollapseOpen(false);
    }
    else {
        const menu = menus?.items?.find(o => o.id === mainMenuPath);

        if (menu) {
            const isSubmenuOpen = menu.children?.some(o => o.url.includes(subMenuPath) || subMenuPath.includes(o.id));

            if (isSubmenuOpen && !collapseOpen) {
                setCollapseOpen(menu.id);
            }
        }
    }

  }

  const collapseIcons = (isOpen) => isOpen ? <ExpandLess sx={{ color: "#fff" }} /> : <ExpandMore sx={{ color: "#fff" }} />;

  const userRoles = Array.isArray(userToken?.role) ? userToken.role.map(value => value.toLowerCase()) : [userToken?.role.toLowerCase()];

  useEffect(() => {
    collapseOpenOrClose(pathname, menuItem);
  }, [pathname, menuItem]);

  return (
    <List>
      {
        menuItem.items.map((item, index) => {
          return applyRoles(item, userRoles) ? (
            <ListItem
              key={item.id}
              disablePadding
              sx={{
                display: 'block'
              }}
              id={item.id}
              onMouseEnter={!drawerOpen ? handlePopoverOpen : undefined}

            >
              <ListItemButtonWrapper
                onClick={item.children?.length > 0 ? (e) => handleCollapse(e) : (e) => handleCollapseAndClick(e, item)}
                selected={collapseOpen === item.id || selectMenuItem(item, pathname)}
                id={item.id}
                sx={[
                  {
                    minHeight: 48,
                    px: 2.5,
                    pl: 2,
                  },
                  drawerOpen
                    ? {
                      justifyContent: 'initial',
                    }
                    : {
                      justifyContent: 'center',
                    },
                ]}
              >

                <ListItemIcon aria-label={t(item.title) + " icon"}
                  sx={[
                    {
                      minWidth: 0,
                      justifyContent: 'center',
                    },
                    drawerOpen
                      ? {
                        mr: 1.5,
                      }
                      : {
                        mr: 'auto',
                      },
                  ]}>
                  {renderAvatar(item)}


                </ListItemIcon>

                <ListItemText
                  primary={t(item.title)}
                  sx={[
                    drawerOpen
                      ? {
                        opacity: 1,
                        "& .MuiTypography-root": {
                          color: "#fff"
                        }
                      }
                      : {
                        opacity: 0,
                      },
                  ]}
                  primaryTypographyProps={{
                    ...item.dataTestId && { "data-testid": item.dataTestId + "Menu" }
                  }}
                />
                {drawerOpen && item?.children?.length ? collapseIcons(collapseOpen === item.id) : null}
              </ListItemButtonWrapper>
              {drawerOpen && item?.children?.length && (
                <Collapse in={collapseOpen === item.id} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}>
                    {item?.children?.map((subItem, index) => {
                      return applyRoles(subItem, userRoles) ? (
                        <Tooltip key={subItem.id} title={t(subItem.tooltip)} placement="right" arrow enterDelay={500} leaveDelay={200}>
                          <ListItemButtonWrapper
                            selected={selectMenuSubItem(subItem, pathname)}
                            sx={{
                              pl: 7,
                            }}
                            onClick={() => handleClick(subItem?.url)}>
                            <ListItemIcon sx={{ color: "#fff" }} aria-label={t(subItem.title) + " icon"}>
                              {createElement(menuIcons[subItem.icon])}
                            </ListItemIcon>
                            <ListItemText primary={t(subItem.title)} sx={{
                              "& .MuiTypography-root": {
                                color: "#fff"
                                }
                              }} 
                              
                              primaryTypographyProps={{
                                ...subItem.dataTestId && { "data-testid": subItem.dataTestId + "Submenu" }
                              }}
                            />
                          </ListItemButtonWrapper>
                        </Tooltip>
                      ) : null;
                    })}
                  </List>
                </Collapse>
              )}
            </ListItem>
          ) : null;
        })
      }
    </List>

  )
}

export function IconMenu({ subMenuList, handleClick }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const { userToken } = useSelector(state => state.sharedState);
  const userRoles = Array.isArray(userToken.role) ? userToken.role.map(value => value.toLowerCase()) : [userToken.role.toLowerCase()];


  return (
    <PopoverPaper elevation={0} >
      <List sx={{ pointerEvents: 'auto' }}>
        <Typography sx={{ p: 2, color: '#fff' }} fontWeight={600}>{t(subMenuList?.title)}</Typography>
        {subMenuList?.items.map((subItem, index) => {
          return applyRoles(subItem, userRoles) ? (
            <Tooltip key={subItem.id} title={t(subItem.tooltip)} placement="right" arrow enterDelay={500} leaveDelay={200}>
              <ListItemButtonWrapper
                onClick={() => handleClick(subItem?.url)}
                key={subItem.id}
                selected={subItem?.url?.includes(pathname.split('/')[3])}
                >
                <ListItemIcon sx={{ color: '#fff' }}>
                  {createElement(menuIcons[subItem.icon])}
                </ListItemIcon>
                <ListItemText primary={t(subItem.title)} sx={{ '& .MuiTypography-root': { color: '#fff' } }} />
              </ListItemButtonWrapper>
            </Tooltip>
          ) : null;
        })}
      </List>
    </PopoverPaper>
  );

}

export function SectionTooltip({ title }) {
  return (
    <Paper
      variant='elevation'
      square
      tabIndex={-1}
      sx={(theme) => ({ padding: 1, backgroundColor: 'rgb(0,0,0)' })}
    >
      <Typography
        fontWeight={600}
        color="#fff"
      >
        {title}
      </Typography>
    </Paper>
  )
}
